/**
 * DrawerBrowsePanel — MS574 Browse tab content for DashboardConciergeDrawer.
 *
 * Renders a recipient-aware catalog grid inside the drawer's Browse tab so
 * the buyer can pick from the catalog without leaving Dashboard. Selecting
 * a SKU opens OrderConfirmSheet with the recipient pre-filled — same sheet
 * /browse uses, just opened from drawer context (cameFrom="dashboard").
 *
 * Why a separate component (not extracting from Browse.tsx):
 *   - Browse.tsx is ~1000 LOC tightly coupled to URL state, page chrome,
 *     popular-picks row, and an editorial category header. A clean
 *     extraction of just the grid would still drag along filter URL sync,
 *     telemetry, and the empty-state CTA wired to /chat — none of which
 *     fit a drawer-overlay context.
 *   - The drawer needs LOCAL filter state (not URL-synced) — closing the
 *     drawer should clear the picker, not leave query params lingering
 *     on /dashboard. Inverting that into a "URL-or-local" branch inside
 *     Browse.tsx would couple two surfaces unnecessarily.
 *   - The catalog query itself is ~15 lines of supabase chaining — small
 *     enough to duplicate, and the drift risk is mitigated by the shared
 *     RLS policy + the shared SkuCard + the shared OrderConfirmSheet.
 *     If a third consumer needs the grid (e.g. wishlist-empty-state
 *     suggestions), THAT is when extraction earns its keep.
 *
 * Scope:
 *   - FilterRail (reused) + SkuCard grid (reused). View →
 *     ProductDetailModal → OrderConfirmSheet via parent-owned
 *     ProductCheckoutModals (portaled above the drawer).
 *   - Recipient context flows in via props: name, id, occasion, optional
 *     dietary_requirements that pre-populates the dietary filter.
 *
 * Telemetry:
 *   - Capture surface stays `browse` (same dashboard funnel as /browse
 *     itself) but the `payload.entry_surface` is annotated `drawer` so
 *     we can split drawer-Browse vs page-Browse in analytics later.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { SkuCard, type SkuCardData } from "@/components/chat/SkuCard";
import { FilterRail, type BrowseFilters } from "@/components/browse/FilterRail";
import { BrowseCatalogToolbar } from "@/components/browse/BrowseCatalogToolbar";
import {
  BROWSE_ALLOWED_CATEGORIES,
  countActiveBrowseFilters,
  defaultBrowseFilters,
  escapeIlikePattern,
} from "@/lib/browseCatalog";
import { deriveBrowseMeta } from "@/lib/deriveBrowseMeta";
import { captureInteraction } from "@/lib/captureInteraction";

// Page size kept small inside the drawer — the surface is narrower
// (500px on desktop) so 12 cards is roughly two full screens of
// scroll; matches the "browse a curated set, don't get lost in the
// catalogue" intent of the drawer (the standalone /browse stays at
// 24 for the full-catalogue scan).
const DRAWER_PAGE_SIZE = 12;

interface VendorProductRow {
  id: string;
  name: string;
  description: string | null;
  price: number | string | null;
  image_url: string | null;
  slug: string | null;
  vendor_category: string | null;
  lead_time_days: number | null;
  is_voucher: boolean | null;
  variant_label: string | null;
  dietary_tags: string[] | null;
  moment_tags: string[] | null;
  vendor_partners: { vendor_name: string } | { vendor_name: string }[] | null;
}

function rowToCard(row: VendorProductRow): SkuCardData {
  const vendor = Array.isArray(row.vendor_partners)
    ? row.vendor_partners[0]
    : row.vendor_partners;
  const isVoucher = Boolean(row.is_voucher);
  return {
    sku_id: row.id,
    name: row.name,
    vendor_name: vendor?.vendor_name ?? "Lembra",
    vendor_category: row.vendor_category ?? "other",
    price_aud: Number(row.price ?? 0),
    image_url: row.image_url,
    slug: row.slug ?? row.id,
    description: row.description,
    is_voucher: isVoucher,
    price_prefix_from: isVoucher && Boolean(row.variant_label),
    lead_time_days: row.lead_time_days,
    dietary_tags: row.dietary_tags,
    highlight_fields: deriveBrowseMeta({
      vendor_category: row.vendor_category ?? "other",
      lead_time_days: row.lead_time_days,
      is_voucher: row.is_voucher ?? false,
      dietary_tags: row.dietary_tags,
      description: row.description,
    }),
  };
}

/** Filter the recipient's known dietary requirements down to the set
 *  the FilterRail actually understands (vegetarian/vegan/gluten_free/
 *  dairy_free/halal/kosher/nut_free per FilterRail.DIETARY). Anything
 *  the rail doesn't render gets dropped silently — passing an
 *  unknown value would result in a query that returns zero rows
 *  with no visible filter chip explaining why. */
const KNOWN_DIETARY = new Set([
  "vegetarian",
  "vegan",
  "gluten_free",
  "dairy_free",
  "halal",
  "kosher",
  "nut_free",
]);

function sanitiseDietary(input: string[] | undefined | null): string[] {
  if (!input || input.length === 0) return [];
  return input.filter((d) => KNOWN_DIETARY.has(d));
}

export interface DrawerBrowsePanelProps {
  /** Recipient context — used for OrderConfirmSheet pre-fill and the
   *  optional dietary auto-filter. */
  recipientName: string;
  recipientId: string;
  occasion?: string;
  /** Optional team_members.dietary_requirements. When present, the
   *  Browse tab opens with those dietary chips pre-selected so the
   *  catalog auto-filters to SKUs that fit the recipient. The buyer
   *  can clear the filter from inside FilterRail to see the full
   *  catalog. */
  dietaryRequirements?: string[];
  /** View handler — parent opens ProductDetailModal via checkout flow. */
  onViewCard: (sku: SkuCardData) => void;
  /** drawer = narrow 2-col grid; fullscreen = dashboard overlay layout. */
  layout?: "drawer" | "fullscreen";
}

export function DrawerBrowsePanel({
  recipientName,
  recipientId,
  dietaryRequirements,
  onViewCard,
  layout = "drawer",
}: DrawerBrowsePanelProps) {
  const isFullscreen = layout === "fullscreen";
  const pageSize = isFullscreen ? 24 : DRAWER_PAGE_SIZE;

  const [filters, setFilters] = useState<BrowseFilters>(() => ({
    ...defaultBrowseFilters(),
    dietary: sanitiseDietary(dietaryRequirements),
  }));

  const [cards, setCards] = useState<SkuCardData[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (offset: number, mode: "initial" | "append") => {
      if (mode === "initial") setLoading(true);
      else setLoadingMore(true);
      setError(null);

      let q = supabase
        .from("vendor_products")
        .select(
          "id, name, description, price, image_url, slug, vendor_category, lead_time_days, is_voucher, variant_label, dietary_tags, moment_tags, vendor_partners(vendor_name)",
          { count: "exact" },
        )
        .is("deleted_at", null)
        .eq("is_available", true)
        .neq("category", "Add-on")
        .is("parent_product_id", null);

      switch (filters.sort) {
        case "price_asc":
          q = q.order("price", { ascending: true }).order("name", { ascending: true });
          break;
        case "price_desc":
          q = q.order("price", { ascending: false }).order("name", { ascending: true });
          break;
        case "name_asc":
          q = q.order("name", { ascending: true });
          break;
        case "newest":
          q = q.order("created_at", { ascending: false, nullsFirst: false }).order("name", { ascending: true });
          break;
        default:
          q = q
            .order("sort_order", { ascending: true, nullsFirst: false })
            .order("created_at", { ascending: false, nullsFirst: false })
            .order("name", { ascending: true });
          break;
      }

      q = q.range(offset, offset + pageSize - 1);

      if (filters.categories.length > 0) {
        q = q.in(
          "vendor_category",
          filters.categories.filter((c) => BROWSE_ALLOWED_CATEGORIES.includes(c)),
        );
      }
      if (filters.priceMin !== null) q = q.gte("price", filters.priceMin);
      if (filters.priceMax !== null) q = q.lte("price", filters.priceMax);
      if (filters.dietary.length > 0) q = q.contains("dietary_tags", filters.dietary);
      if (filters.maxLeadTimeDays !== null) {
        q = q.or(
          `lead_time_days.lte.${filters.maxLeadTimeDays},lead_time_days.is.null,is_voucher.eq.true`,
        );
      }
      if (filters.momentTag) {
        q = q.or(`moment_tags.cs.{${filters.momentTag}},moment_tags.is.null`);
      }
      const search = filters.searchQuery.trim();
      if (search) {
        const pattern = `%${escapeIlikePattern(search)}%`;
        q = q.or(`name.ilike.${pattern},description.ilike.${pattern}`);
      }

      const { data, error: err, count } = await q;
      if (err) {
        setError("We couldn't load the catalogue just now. Try again in a moment.");
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const newCards = (data ?? []).map(rowToCard);
      if (mode === "initial") {
        setCards(newCards);
        setLoaded(newCards.length);
      } else {
        setCards((prev) => [...prev, ...newCards]);
        setLoaded((prev) => prev + newCards.length);
      }
      setTotalCount(typeof count === "number" ? count : null);
      setLoading(false);
      setLoadingMore(false);
    },
    [filters, pageSize],
  );

  // Re-fetch from offset 0 whenever filters change.
  useEffect(() => {
    void fetchPage(0, "initial");
  }, [fetchPage]);

  const handleFilterChange = (next: BrowseFilters) => {
    void captureInteraction({
      surface: "browse",
      event_type: "filter_changed",
      payload: { next, prev: filters, entry_surface: "drawer" },
      recipient_id: recipientId ?? null,
    });
    setFilters(next);
  };

  const handleLoadMore = () => {
    if (loadingMore || loaded >= (totalCount ?? Infinity)) return;
    void captureInteraction({
      surface: "browse",
      event_type: "load_more",
      payload: { offset: loaded, total_loaded: loaded, entry_surface: "drawer" },
      recipient_id: recipientId ?? null,
    });
    void fetchPage(loaded, "append");
  };

  // Selecting a SKU opens the product detail modal (parent-owned
  // checkout flow portaled above the drawer).
  const handleSelectCard = (sku: SkuCardData) => {
    void captureInteraction({
      surface: "browse",
      event_type: "card_selected",
      payload: { sku_id: sku.sku_id, slug: sku.slug, entry_surface: "drawer" },
      recipient_id: recipientId ?? null,
      vendor_product_id: sku.sku_id,
    });
    onViewCard(sku);
  };

  const hasActiveFilters = countActiveBrowseFilters(filters) > 0;

  return (
    <div
      className={
        isFullscreen
          ? "flex flex-col gap-4 px-4 py-4 sm:px-8 sm:py-6 max-w-6xl mx-auto w-full"
          : "flex flex-col gap-4 p-3"
      }
      data-testid={isFullscreen ? "drawer-browse-panel-fullscreen" : "drawer-browse-panel"}
      style={{ fontFamily: "DM Sans, sans-serif" }}
    >
      {/* Result summary line — no link to /chat (the buyer's already
          IN the drawer; the Concierge tab is right next door). */}
      <div className="text-xs text-charcoal/65" data-testid="drawer-browse-summary">
        {loading ? (
          "Loading…"
        ) : totalCount !== null ? (
          <>
            <span className="font-semibold text-navy">{totalCount}</span>{" "}
            {totalCount === 1 ? "result" : "results"}
            {filters.dietary.length > 0 && (
              <span className="text-charcoal/55"> · filtered for {recipientName}</span>
            )}
          </>
        ) : (
          `${cards.length} loaded`
        )}
      </div>

      {/* FilterRail — the same component used on /browse. Renders the
          mobile-style "Filters" toggle by default (the drawer is
          narrow enough that the desktop persistent rail wouldn't fit
          alongside the grid). The rail's mobile mode is exactly what
          we want: a toggle button + collapsible panel. */}
      <FilterRail
        value={filters}
        onChange={handleFilterChange}
        variant={isFullscreen ? "embedded" : "default"}
      />

      <BrowseCatalogToolbar
        filters={filters}
        onChange={handleFilterChange}
        totalCount={totalCount}
        loading={loading}
        variant="compact"
      />

      {error && (
        <div
          className="rounded-2xl border border-coral/40 bg-coral/5 px-3 py-2 text-xs text-coral"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Grid — 2 columns to fit the drawer's narrow width. Skeleton
          placeholders during initial load mirror /browse so the panel
          doesn't flash blank on a slow connection. */}
      {loading && cards.length === 0 ? (
        <div className="grid grid-cols-2 gap-2" data-testid="drawer-browse-skeletons">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl border border-charcoal/8"
            >
              <Skeleton className="aspect-[4/3] w-full rounded-none" />
              <div className="space-y-2 p-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : !loading && cards.length === 0 && !error ? (
        <div
          className="rounded-2xl border border-navy/10 bg-white px-4 py-8 text-center"
          data-testid="drawer-browse-empty"
        >
          <p className="text-sm font-semibold text-navy">Nothing matches yet</p>
          <p className="mt-2 text-xs text-charcoal/65">
            Widen the filters, or switch to the Concierge tab to talk it through.
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() =>
                handleFilterChange({
                  ...defaultBrowseFilters(),
                  dietary: sanitiseDietary(dietaryRequirements),
                })
              }
              className="mt-3 inline-flex rounded-full border border-navy/20 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div
          className={
            isFullscreen
              ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
              : "grid grid-cols-2 gap-2"
          }
          data-testid="drawer-browse-grid"
        >
          {cards.map((sku, index) => (
            <SkuCard
              key={sku.sku_id}
              sku={sku}
              variant="grid"
              onView={handleSelectCard}
              // Eagerly load the first row (2 cards) — same reasoning
              // as Browse.tsx's first-row priority but scaled to the
              // drawer's 2-col layout.
              priority={index < 2}
              surface="browse"
            />
          ))}
        </div>
      )}

      {!loading && totalCount !== null && loaded < totalCount && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="rounded-full border-2 border-navy/15 bg-white px-4 py-2 text-xs font-semibold text-navy hover:border-coral hover:text-coral disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-coral"
          >
            {loadingMore ? "Loading…" : `Load more (${totalCount - loaded} left)`}
          </button>
        </div>
      )}
    </div>
  );
}
